import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Per-route page metadata for SEO. Updates document.title and meta description
// on every navigation so each page gets a unique, indexable title.
const PAGE_META = {
  '/': {
    title: 'Our Soil Our Soul — Revitalising the Nature | Sustainable Agriculture India',
    description: 'OUR SOIL (OPC) PRIVATE LIMITED — ISO 9001:2015 Certified. Empowering Indian farmers through sustainable agriculture, soil health, organic farming, and community support.',
  },
  '/home': {
    title: 'Home — Our Soil Our Soul | Sustainable Agriculture & Soil Health',
    description: 'Welcome to Our Soil Our Soul. Explore the virtuous cycle of soil health, our mission, and our work across India.',
  },
  '/about': {
    title: 'About Us — Our Soil Our Soul | Our Mission & Values',
    description: 'Learn how Our Soil Our Soul revitalises nature through sustainable agriculture. Discover our founder story, company background, values, and advisory team.',
  },
  '/why-soil-health': {
    title: 'Why Soil Health Matters — Our Soil Our Soul',
    description: 'A simple scientific explanation of soil health: living capacity of soil, organic carbon, microbial biomass, structure, pH, and water holding capacity.',
  },
  '/our-approach': {
    title: 'Our Approach — Our Soil Our Soul | Soil Testing, Biological Inputs, Natural Farming',
    description: 'Five integrated pillars: soil testing, biological inputs, natural farming, training, and demonstrations — designed to revitalise soil and empower farmers.',
  },
  '/services': {
    title: 'Services — Our Soil Our Soul | Farmer Advisory, FPO Support, Market Linkage',
    description: 'Farmer advisory, institutional consulting, FPO support, training programs, and market linkage for sustainable agriculture in India.',
  },
  '/field-stories': {
    title: 'Field Stories — Our Soil Our Soul | Case Studies & Farmer Testimonials',
    description: 'Real farms, real results. Case studies, farmer testimonials, and trial photos from our pilot farms and partner FPOs.',
  },
  '/community': {
    title: 'Farmer Community — Our Soil Our Soul | Join 10,000+ Farmers',
    description: 'Connect with sustainable farmers across India. Share knowledge, attend events, and access training and support.',
  },
  '/knowledge-hub': {
    title: 'Knowledge Hub — Our Soil Our Soul | Articles & Videos',
    description: 'Articles, field guides, and video resources on soil health, sustainable agriculture, organic farming techniques, and community impact stories.',
  },
  '/blog': {
    title: 'Knowledge Hub — Our Soil Our Soul | Articles & Videos',
    description: 'Articles, field guides, and video resources on soil health, sustainable agriculture, organic farming techniques, and community impact stories.',
  },
  '/partners': {
    title: 'Partners — Our Soil Our Soul | Universities, NGOs, FPOs, Companies',
    description: 'We partner with universities, NGOs, farmer producer organisations (FPOs), companies, and subject-matter experts to scale soil regeneration across India.',
  },
  '/contact': {
    title: 'Contact Us — Our Soil Our Soul | Get in Touch',
    description: 'Reach Our Soil (OPC) Private Limited — H.No 7-200, Sanjay Gandhi Nagar, Shapur Nagar, IDA Jeedimetla, Medchal District, Telangana. Phone: +91 80967 28127.',
  },
  '/privacy': {
    title: 'Privacy Policy — Our Soil Our Soul',
    description: 'Privacy policy for oursoiloursoul.in and the Our Soil Our Soul application.',
  },
};

const setMeta = (name, content) => {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setProperty = (property, content) => {
  if (!content) return;
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

export const SEORouteHandler = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    // Match exact, or fall back to blog-detail pattern, or homepage default
    let meta = PAGE_META[path];
    if (!meta && path.startsWith('/blog/')) {
      meta = {
        title: 'Blog Post — Our Soil Our Soul',
        description: 'Read the latest article from Our Soil Our Soul on sustainable agriculture and soil health.',
      };
    }
    if (!meta) meta = PAGE_META['/'];

    document.title = meta.title;
    setMeta('description', meta.description);
    setMeta('twitter:title', meta.title);
    setMeta('twitter:description', meta.description);
    setProperty('og:title', meta.title);
    setProperty('og:description', meta.description);
    setProperty('og:url', `https://oursoiloursoul.in${path}`);
    setCanonical(`https://oursoiloursoul.in${path}`);

    // Smooth-scroll to anchor on navigation (so /#about, /#services etc. work cleanly)
    if (location.hash) {
      setTimeout(() => {
        const id = location.hash.slice(1);
        const target = document.getElementById(id);
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, [location.pathname, location.hash]);

  return null;
};
